'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/platform/auth/useAuth';
import { EmojiReactionButton } from '@/platform/reactions/EmojiReactionButton';
import { emojiFromApiResponse } from '@/platform/reactions/emojiReactionLogic';
import { getForumCommentEmoji, getForumPostEmoji, setCommentEmoji, setPostEmoji } from './api';
import './forum-actions.css';

type Props = {
  postId: string;
  commentId?: string;
  onUpdate?: () => void;
  /** Product tour: no API — local emoji toggle only. */
  localOnly?: boolean;
  initialEmoji?: string | null;
};

export function EmojiReactions({
  postId,
  commentId,
  onUpdate,
  localOnly = false,
  initialEmoji = null,
}: Props) {
  const { isAuthenticated } = useAuth();
  const [selected, setSelected] = useState<string | null>(
    localOnly ? emojiFromApiResponse(initialEmoji) : null,
  );
  const loadGenRef = useRef(0);

  const refreshSelected = useCallback(async () => {
    if (localOnly || !isAuthenticated) {
      if (!localOnly) setSelected(null);
      return;
    }
    const gen = ++loadGenRef.current;
    try {
      const res = commentId
        ? await getForumCommentEmoji(postId, commentId)
        : await getForumPostEmoji(postId);
      if (loadGenRef.current === gen) {
        setSelected(emojiFromApiResponse(res.emoji));
      }
    } catch {
      if (loadGenRef.current === gen) {
        setSelected(null);
      }
    }
  }, [postId, commentId, isAuthenticated, localOnly]);

  useEffect(() => {
    void refreshSelected();
  }, [refreshSelected]);

  async function react(emoji: string | null) {
    if (localOnly) {
      setSelected(emojiFromApiResponse(emoji ?? ''));
      onUpdate?.();
      return;
    }
    if (!isAuthenticated) return;
    loadGenRef.current += 1;
    const opGen = loadGenRef.current;
    const previous = selected;
    const next = emojiFromApiResponse(emoji ?? '');
    setSelected(next);
    try {
      const res = commentId
        ? await setCommentEmoji(postId, commentId, emoji ?? '')
        : await setPostEmoji(postId, emoji ?? '');
      if (loadGenRef.current === opGen) {
        setSelected(emojiFromApiResponse(res.emoji));
      }
      onUpdate?.();
    } catch {
      if (loadGenRef.current === opGen) {
        setSelected(previous);
        await refreshSelected();
      }
    }
  }

  return (
    <EmojiReactionButton
      disabled={!localOnly && !isAuthenticated}
      selectedEmoji={selected}
      onReact={react}
      ariaLabel={commentId ? 'React to reply' : 'React to post'}
    />
  );
}
